import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiArrowLeft, FiUploadCloud } from 'react-icons/fi';
import { apiUrl } from '../../config/api';
import { VIDEO_GET, VIDEO_UPLOAD, VIDEO_THUMBNAIL_UPLOAD, VIDEO_CREATE, PLAYLIST_LIST, CATEGORY_LIST } from '../../api/contracts';
import { getApiErrorMessage } from '../../utils/errorMessage';
import MultiSelect from '../MultiSelect';
import './Page.css';
import './CreateCategory.css';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const AGE_BUCKET_OPTIONS = [
  { value: '0-3', label: '0-3' },
  { value: '3-6', label: '3-6' },
  { value: '6+', label: '6+' },
];

const CONTENT_TYPE_OPTIONS = [
  { value: 'Licensed', label: 'Licensed' },
  { value: 'Original', label: 'Original' },
];

function CreateVideo() {
  const navigate = useNavigate();
  const location = useLocation();
  const duplicateId = location.state?.duplicateId;
  const [title, setTitle] = useState('');
  const [channel_name, setChannel_name] = useState('');
  const [feed_key, setFeed_key] = useState('');
  const [status, setStatus] = useState('active');
  const [video_url, setVideo_url] = useState('');
  const [videoFile, setVideoFile] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [thumbnail_url, setThumbnail_url] = useState('');
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [age_buckets, setAge_buckets] = useState([]);
  const [content_types, setContent_types] = useState([]);
  const [playlist_ids, setPlaylist_ids] = useState([]);
  const [category_ids, setCategory_ids] = useState([]);
  const [playlistOptions, setPlaylistOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingDuplicate, setLoadingDuplicate] = useState(!!duplicateId);

  useEffect(() => {
    if (!duplicateId) return;
    let cancelled = false;
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(apiUrl(VIDEO_GET.pathFor(duplicateId)), { method: VIDEO_GET.method, headers })
      .then((res) => {
        if (!res.ok) throw new Error(res.status === 404 ? 'Video not found.' : `Request failed (${res.status}).`);
        return res.json();
      })
      .then((json) => {
        if (cancelled) return;
        const data = json.data || json;
        setTitle(data.title ?? '');
        setChannel_name(data.channel_name ?? '');
        setFeed_key(data.feed_key ?? '');
        setStatus((data.status || 'active').toLowerCase());
        setVideo_url(data.video_url ?? '');
        setThumbnail_url(data.thumbnail_url ?? '');
        if (data.thumbnail_url) setThumbnailPreview(data.thumbnail_url);
        setAge_buckets(Array.isArray(data.age_buckets) ? data.age_buckets.map((v) => (v === '<3' ? '0-3' : v)) : []);
        setContent_types(Array.isArray(data.content_types) ? data.content_types : []);
        setPlaylist_ids(Array.isArray(data.playlist_ids) ? data.playlist_ids : []);
        setCategory_ids(Array.isArray(data.category_ids) ? data.category_ids : []);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || 'Unable to load video. Please try again.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingDuplicate(false);
      });
    return () => { cancelled = true; };
  }, [duplicateId]);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    Promise.all([
      fetch(apiUrl(PLAYLIST_LIST.path), { headers }).then((r) => r.json()).catch(() => ({ data: [] })),
      fetch(apiUrl(CATEGORY_LIST.path), { headers }).then((r) => r.json()).catch(() => ({ data: [] })),
    ]).then(([plRes, catRes]) => {
      if (cancelled) return;
      setPlaylistOptions((plRes.data || []).map((p) => ({ value: p.id || p.title, label: p.title || p.id || '—' })));
      setCategoryOptions((catRes.data || []).map((c) => ({ value: c.id, label: c.name || c.id || '—' })));
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    return () => {
      if (thumbnailPreview && thumbnailPreview.startsWith('blob:')) URL.revokeObjectURL(thumbnailPreview);
    };
  }, [thumbnailPreview]);

  const handleVideoFileChange = async (e) => {
    const file = e.target.files?.[0];
    setVideoFile(file || null);
    setError('');
    if (!file) {
      setVideo_url('');
      return;
    }
    if (!file.type.startsWith('video/')) {
      setError('Please select a video file.');
      setVideoFile(null);
      setVideo_url('');
      return;
    }

    setUploadingVideo(true);
    try {
      const formData = new FormData();
      formData.append('video', file);
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(apiUrl(VIDEO_UPLOAD.path), {
        method: VIDEO_UPLOAD.method,
        headers,
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(getApiErrorMessage(data, res.status, { default: 'Video upload failed. Please try again.' }));
        setVideo_url('');
        setUploadingVideo(false);
        return;
      }
      const uploadedUrl = data.data?.video_url || data.video_url || '';
      if (!uploadedUrl) {
        setError('Video uploaded but no video URL returned.');
        setVideo_url('');
        setUploadingVideo(false);
        return;
      }
      setVideo_url(uploadedUrl);
    } catch (err) {
      setError(err.message || 'Unable to upload video. Please try again.');
      setVideo_url('');
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleThumbnailChange = async (e) => {
    const file = e.target.files?.[0];
    setError('');

    if (thumbnailPreview && thumbnailPreview.startsWith('blob:')) {
      URL.revokeObjectURL(thumbnailPreview);
      setThumbnailPreview(null);
    }

    if (!file) {
      setThumbnailFile(null);
      setThumbnail_url('');
      return;
    }
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file for thumbnail.');
      setThumbnailFile(null);
      setThumbnail_url('');
      return;
    }

    setThumbnailFile(file);
    setThumbnailPreview(URL.createObjectURL(file));

    setUploadingThumbnail(true);
    try {
      const formData = new FormData();
      formData.append('thumbnail', file);
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(apiUrl(VIDEO_THUMBNAIL_UPLOAD.path), {
        method: VIDEO_THUMBNAIL_UPLOAD.method,
        headers,
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(getApiErrorMessage(data, res.status, { default: 'Thumbnail upload failed. Please try again.' }));
        setThumbnail_url('');
        setUploadingThumbnail(false);
        return;
      }
      const uploadedUrl = data.data?.thumbnail_url || data.thumbnail_url || '';
      if (!uploadedUrl) {
        setError('Thumbnail uploaded but no thumbnail URL returned.');
        setThumbnail_url('');
        setUploadingThumbnail(false);
        return;
      }
      setThumbnail_url(uploadedUrl);
    } catch (err) {
      setError(err.message || 'Unable to upload thumbnail. Please try again.');
      setThumbnail_url('');
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError('Title is required.');
      return;
    }

    const finalVideoUrl = (video_url || '').trim();
    if (!finalVideoUrl) {
      setError('Please upload a video.');
      return;
    }

    const finalThumbUrl = (thumbnail_url || '').trim();
    if (!finalThumbUrl) {
      setError('Please upload a thumbnail image.');
      return;
    }

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const body = {
        title: trimmedTitle,
        channel_name: channel_name.trim() || undefined,
        feed_key: feed_key.trim() || undefined,
        status,
        thumbnail_url: finalThumbUrl,
        video_url: finalVideoUrl,
        age_buckets: age_buckets.length ? age_buckets : undefined,
        content_types: content_types.length ? content_types : undefined,
        playlist_ids: playlist_ids.length ? playlist_ids : undefined,
        category_ids: category_ids.length ? category_ids : undefined,
      };
      const res = await fetch(apiUrl(VIDEO_CREATE.path), {
        method: VIDEO_CREATE.method,
        headers,
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(getApiErrorMessage(data, res.status));
        setIsSubmitting(false);
        return;
      }
      navigate('/dashboard/cms/video');
    } catch (err) {
      setError(err.message || 'Unable to connect. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page create-category-page">
      <button type="button" className="create-category-back" onClick={() => navigate('/dashboard/cms/video')}>
        <FiArrowLeft className="back-icon" aria-hidden />
        Back to Video
      </button>

      <div className="create-category-header">
        <h3>{duplicateId ? 'Duplicate Video' : 'Create Video'}</h3>
        <p className="create-category-subtitle">
          {duplicateId
            ? 'Edit details below and save to create a new video (same video/thumbnail URLs by default).'
            : 'Add a new video. Upload a video file or enter a video URL. Map to playlists and categories as needed.'}
        </p>
      </div>

      {loadingDuplicate && <p className="create-category-loading">Loading video…</p>}

      <form onSubmit={handleSubmit} className="create-category-form" style={loadingDuplicate ? { opacity: 0.6, pointerEvents: 'none' } : undefined}>
        <div className="form-page-layout">
          <div className="form-section">
            <h4 className="form-section-title">Video</h4>
            <div className="form-row-single">
              <label>Upload video <span className="label-required" aria-hidden="true">*</span></label>
              <div className="thumbnail-upload-area">
                <input
                  type="file"
                  id="video-upload"
                  accept="video/*"
                  onChange={handleVideoFileChange}
                  disabled={isSubmitting || uploadingVideo || uploadingThumbnail}
                  className="thumbnail-input"
                />
                <label htmlFor="video-upload" className="thumbnail-upload-label">
                  <FiUploadCloud className="upload-icon" aria-hidden />
                  <span>{videoFile ? videoFile.name : 'Choose video file'}</span>
                </label>
              </div>
              <p className="form-hint">{uploadingVideo ? 'Uploading video…' : video_url ? 'Uploaded.' : 'Select a video file to upload.'}</p>
            </div>
            <div className="form-row-single">
              <label htmlFor="video-url">Video (uploaded)</label>
              <input
                id="video-url"
                type="text"
                value={video_url}
                readOnly
                placeholder="Upload video to generate URL"
                disabled
              />
            </div>
          </div>

          <div className="form-section">
            <h4 className="form-section-title">Details</h4>
            <div className="form-grid-2">
              <div className="form-row-single">
                <label htmlFor="video-title">Title <span className="label-required" aria-hidden="true">*</span></label>
                <input
                  id="video-title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Lets_Learn_About_Vehicles"
                  disabled={isSubmitting}
                  autoComplete="off"
                />
              </div>
              <div className="form-row-single">
                <label htmlFor="video-status">Status</label>
                <select id="video-status" value={status} onChange={(e) => setStatus(e.target.value)} disabled={isSubmitting}>
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-row-single">
                <label htmlFor="video-channel">Channel name</label>
                <input
                  id="video-channel"
                  type="text"
                  value={channel_name}
                  onChange={(e) => setChannel_name(e.target.value)}
                  placeholder="e.g. kidzoki"
                  disabled={isSubmitting}
                  autoComplete="off"
                />
              </div>
              <div className="form-row-single">
                <label htmlFor="video-feed">Feed key</label>
                <input
                  id="video-feed"
                  type="text"
                  value={feed_key}
                  onChange={(e) => setFeed_key(e.target.value)}
                  placeholder="e.g. FEED"
                  disabled={isSubmitting}
                  autoComplete="off"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h4 className="form-section-title">Thumbnail <span className="label-required" aria-hidden="true">*</span></h4>
            <div className="form-thumbnail-with-preview">
              <div className="thumbnail-upload-area">
                <input
                  type="file"
                  id="video-thumbnail-upload"
                  accept="image/*"
                  onChange={handleThumbnailChange}
                  disabled={isSubmitting || uploadingVideo || uploadingThumbnail}
                  className="thumbnail-input"
                />
                <label htmlFor="video-thumbnail-upload" className="thumbnail-upload-label">
                  <FiUploadCloud className="upload-icon" aria-hidden />
                  <span>{thumbnailFile ? thumbnailFile.name : 'Choose thumbnail image'}</span>
                </label>
                <p className="form-hint">{uploadingThumbnail ? 'Uploading thumbnail…' : thumbnail_url ? 'Uploaded.' : 'Select an image to upload.'}</p>
              </div>
              {thumbnailPreview && (
                <div className="thumbnail-preview-wrap">
                  <img src={thumbnailPreview} alt="Thumbnail preview" className="thumbnail-preview-img" />
                </div>
              )}
            </div>
            <div className="form-row-single" style={{ marginTop: 16 }}>
              <label htmlFor="video-thumb-url">Thumbnail (uploaded)</label>
              <input id="video-thumb-url" type="text" value={thumbnail_url} readOnly disabled placeholder="Upload thumbnail to generate URL" />
            </div>
          </div>

          <div className="form-section form-section-full">
            <h4 className="form-section-title">Mapping (optional)</h4>
            <div className="form-grid-3">
              <div className="form-row-single">
                <label>Age buckets</label>
                <MultiSelect
                  options={AGE_BUCKET_OPTIONS}
                  value={age_buckets}
                  onChange={setAge_buckets}
                  placeholder="Select age groups"
                  disabled={isSubmitting}
                />
              </div>
              <div className="form-row-single">
                <label>Content types</label>
                <MultiSelect
                  options={CONTENT_TYPE_OPTIONS}
                  value={content_types}
                  onChange={setContent_types}
                  placeholder="Select content types"
                  disabled={isSubmitting}
                />
              </div>
              <div className="form-row-single" />
            </div>
            <div className="form-grid-2" style={{ marginTop: 20 }}>
              <div className="form-row-single">
                <label>Categories</label>
                <MultiSelect
                  options={playlistOptions}
                  value={playlist_ids}
                  onChange={setPlaylist_ids}
                  placeholder="Select categories (multiple)"
                  disabled={isSubmitting}
                />
              </div>
              <div className="form-row-single">
                <label>Parent categories</label>
                <MultiSelect
                  options={categoryOptions}
                  value={category_ids}
                  onChange={setCategory_ids}
                  placeholder="Select parent categories (multiple)"
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>
        </div>

        {error && <div className="create-category-error" role="alert">{error}</div>}

        <div className="form-actions form-actions-full">
          <button type="button" className="btn-cancel" onClick={() => navigate('/dashboard/cms/video')} disabled={isSubmitting}>
            Cancel
          </button>
          <button type="submit" className="btn-submit" disabled={isSubmitting || uploadingVideo || uploadingThumbnail}>
            {uploadingVideo || uploadingThumbnail ? 'Uploading…' : isSubmitting ? 'Creating…' : 'Create Video'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default CreateVideo;
